/**
 * Real cryptography on the mock side.
 *
 * The mock backend uses actual Ed25519 keys, real SHA-256 hashes, and
 * canonical-JSON-style serialization so that every mock-produced
 * manifest/signature *is* cryptographically valid. When the real Rust
 * backend replaces the mock, a `.gi` file produced by the mock will
 * still verify — nothing about the shape changes.
 *
 * This keeps the UI's "signature valid" pill honest during prototyping
 * and makes the Spark moment non-fake: the browser actually signs.
 */

import { ed25519 } from '@noble/curves/ed25519';
import { sha256 } from '@noble/hashes/sha256';
import { bytesToHex, randomBytes } from '@noble/hashes/utils';

import type { Ed25519Signature, Hash256, PublicKey } from '../types';

export interface KeyPair {
  public_key: PublicKey;
  secret_key: Uint8Array;
}

/** Generate a new Ed25519 keypair using the browser's crypto RNG. */
export function generateKeyPair(): KeyPair {
  const secret_key = ed25519.utils.randomPrivateKey();
  const pk = ed25519.getPublicKey(secret_key);
  return {
    public_key: `ed25519:${bytesToHex(pk)}`,
    secret_key,
  };
}

/** Sign a 32-byte digest; returns the tagged signature. */
export function signDigest(digest: Uint8Array, kp: KeyPair): Ed25519Signature {
  const sig = ed25519.sign(digest, kp.secret_key);
  return `sig:${bytesToHex(sig)}`;
}

/** SHA-256, packaged as the tagged wire form. */
export function hashBytes(bytes: Uint8Array): Hash256 {
  return `sha256:${bytesToHex(sha256(bytes))}`;
}

/**
 * Canonicalize-and-hash approximation.
 *
 * `JSON.stringify` with sorted keys is NOT RFC 8785 JCS — it skips the
 * codepoint-sort rules the spec requires. The real engine uses
 * `serde_jcs`. For the mock this is "close enough": two identical
 * inputs hash to identical outputs, which is what the UI needs for
 * Spark → Verify round-trip proof. When the real engine signs a `.gi`,
 * verification happens Rust-side with the real canonicalizer.
 */
export function canonicalHash(value: unknown): Hash256 {
  const json = canonicalJsonStringify(value);
  const bytes = new TextEncoder().encode(json);
  return hashBytes(bytes);
}

/** Deterministic JSON with sorted object keys + no whitespace. */
export function canonicalJsonStringify(value: unknown): string {
  return stringify(value);
}

function stringify(v: unknown): string {
  if (v === null) return 'null';
  if (typeof v === 'number') {
    if (!Number.isFinite(v)) throw new Error('non-finite number in canonical JSON');
    return JSON.stringify(v);
  }
  if (typeof v === 'boolean' || typeof v === 'string') return JSON.stringify(v);
  if (Array.isArray(v)) return '[' + v.map(stringify).join(',') + ']';
  if (typeof v === 'object') {
    const obj = v as Record<string, unknown>;
    const keys = Object.keys(obj).sort();
    return (
      '{' +
      keys
        .filter((k) => obj[k] !== undefined)
        .map((k) => JSON.stringify(k) + ':' + stringify(obj[k]))
        .join(',') +
      '}'
    );
  }
  throw new Error(`cannot canonicalize value of type ${typeof v}`);
}

/** Hex-encode random bytes (helper for demo data generation). */
export function randomHex(length: number): string {
  return bytesToHex(randomBytes(length));
}
